import type { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import { ALPHA, NEUTRAL, STATUS_BADGE, SURFACE } from "../../../../theme/tokens";
import { deepLinkWashAnimation, deepLinkWashReducedMotion } from "../../Animations/deepLinkWash";

/*
 * Chrome for the dense, inline-edit config tables (Break Rules, Overtime, Hours Rate, Bonus Plan,
 * Auto-approve, shift lengths, HRIS sources). Moved out of the Department Configuration page in
 * DAT-905 because the wage rule tables now render on Team → Wage Settings as well, and both
 * surfaces must read as one family.
 */

// Precomputed to static rgba so it survives object-spreading into SystemDropdown's
// `selectSx` (a theme-callback sx would be dropped by the spread).
const BRAND_FOCUS_RING = `0 0 0 2px ${alpha(STATUS_BADGE.BRAND_FALLBACK_HEX, ALPHA.FOCUS_RING)}`;

// Shared card surface matching the SettingsSection family.
const SURFACE_CARD_SX = {
	border: `1px solid ${NEUTRAL[200]}`,
	borderRadius: 2,
	backgroundColor: SURFACE,
} as const;

// Shared chrome for the dense Rates-tab tables, modeled on `SystemDataGrid` so all four
// (Break Rules / Overtime / Bonus Plan / Hours Rate) stay cohesive.
export const CONFIG_TABLE_CONTAINER_SX = {
	...SURFACE_CARD_SX,
	height: "max-content",
	flex: "0 0 auto",
	width: "100%",
	overflowX: "auto",
} as const;

// Header cell mirroring SystemDataGrid's column-header treatment. Callback sx so it can
// resolve `alpha()` against the live theme.
export const CONFIG_TABLE_HEADER_CELL_SX = (theme: Theme): SystemStyleObject<Theme> => ({
	backgroundColor: alpha(theme.palette.primary.light, 0.4),
	color: "primary.dark",
	fontWeight: 600,
	borderBottom: `1px solid ${NEUTRAL[200]}`,
	py: 1.25,
	px: 1.5,
	whiteSpace: "nowrap",
});

// Body cell with a hairline bottom divider. The last row's divider is the container border.
export const CONFIG_TABLE_BODY_CELL_SX = {
	borderBottom: `1px solid ${NEUTRAL[150]}`,
	py: 0.75,
	px: 1,
	verticalAlign: "middle",
} as const;

// Body row hover wash, gated behind a real pointer and reduced-motion-safe.
const BODY_ROW_STYLE: SystemStyleObject<Theme> = {
	"@media (hover: hover) and (pointer: fine)": {
		"&:hover": { backgroundColor: NEUTRAL[100] },
	},
	"& .MuiTableCell-root": { transition: "background-color 150ms ease-out" },
	"@media (prefers-reduced-motion: reduce)": {
		"& .MuiTableCell-root": { transition: "none" },
	},
};
export const CONFIG_TABLE_BODY_ROW_SX: SxProps<Theme> = BODY_ROW_STYLE;

/** The body row, optionally carrying the "you landed here" wash (`deepLinkWashStyle`) for a row a deep link pointed at. */
export const configTableBodyRowSx = (highlighted: boolean): SxProps<Theme> =>
	highlighted
		? (theme) => ({
				...BODY_ROW_STYLE,
				...deepLinkWashAnimation(theme),
				// One reduced-motion block holding both the row's and the wash's rules, so neither replaces the other.
				"@media (prefers-reduced-motion: reduce)": {
					"& .MuiTableCell-root": { transition: "none" },
					...deepLinkWashReducedMotion(theme),
				},
			})
		: BODY_ROW_STYLE;

// ── Borderless / seamless in-cell inputs ────────────────────────────────────────────
// The value reads as plain text filling the cell (Airtable / Linear inline-edit feel):
// no resting outline, hover wash, filled + brand-ring on focus.

/** Outlined MUI `<TextField>`/`<Select>` rendered borderless inside a grid cell. */
export const BORDERLESS_INPUT_SX: SxProps<Theme> = (theme) => ({
	"& .MuiOutlinedInput-root": {
		borderRadius: 1,
		transition: "background-color 150ms ease-out",
		"& fieldset": { border: "none" },
		"@media (hover: hover) and (pointer: fine)": {
			"&:hover": { backgroundColor: NEUTRAL[100] },
		},
		"&.Mui-focused": {
			backgroundColor: NEUTRAL[50],
			boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}`,
		},
	},
	"@media (prefers-reduced-motion: reduce)": {
		"& .MuiOutlinedInput-root": { transition: "none" },
	},
});

/** `SystemDropdown` `selectSx` override — borderless select that shows the value/chips
 *  as plain text with just the chevron, hover wash, and a focus ring. */
// NOTE: must be a PLAIN OBJECT (not a theme callback). SystemDropdown applies `selectSx`
// by object-spreading it (`sx: { ...selectSx, … }`), which silently drops a function sx.
export const BORDERLESS_SELECT_SX: SystemStyleObject<Theme> = {
	borderRadius: 1,
	transition: "background-color 150ms ease-out",
	"& .MuiOutlinedInput-notchedOutline": { border: "none" },
	"@media (hover: hover) and (pointer: fine)": {
		"&:hover": { backgroundColor: NEUTRAL[100] },
	},
	"&.Mui-focused": {
		backgroundColor: NEUTRAL[50],
		boxShadow: BRAND_FOCUS_RING,
	},
	"@media (prefers-reduced-motion: reduce)": { transition: "none" },
};

/** `SystemAutoComplete` `textFieldProps.sx` — borderless time field inside a grid cell.
 *  Mirrors BORDERLESS_INPUT_SX but scoped to the autocomplete's outlined input. */
export const BORDERLESS_AUTOCOMPLETE_SX: SxProps<Theme> = (theme) => ({
	"& .MuiOutlinedInput-root": {
		borderRadius: 1,
		transition: "background-color 150ms ease-out",
		"& .MuiOutlinedInput-notchedOutline": { border: "none" },
		"@media (hover: hover) and (pointer: fine)": {
			"&:hover": { backgroundColor: NEUTRAL[100] },
		},
		"&.Mui-focused": {
			backgroundColor: NEUTRAL[50],
			boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}`,
		},
		"&.Mui-error": {
			boxShadow: `0 0 0 2px ${alpha(theme.palette.error.main, ALPHA.FOCUS_RING)}`,
		},
	},
	"@media (prefers-reduced-motion: reduce)": {
		"& .MuiOutlinedInput-root": { transition: "none" },
	},
});

/** `TimeRangeListField` `fieldSx` — the same borderless cell treatment for an x-date-pickers field.
 *  A separate const because the pickers render `.MuiPickersOutlinedInput-*`, not `.MuiOutlinedInput-*`:
 *  passing BORDERLESS_AUTOCOMPLETE_SX here matches nothing and silently leaves the outline on. */
export const BORDERLESS_PICKER_SX: SxProps<Theme> = (theme) => ({
	"& .MuiPickersOutlinedInput-root": {
		borderRadius: 1,
		transition: "background-color 150ms ease-out",
		"& .MuiPickersOutlinedInput-notchedOutline": { border: "none" },
		"@media (hover: hover) and (pointer: fine)": {
			"&:hover": { backgroundColor: NEUTRAL[100] },
		},
		"&.Mui-focused": {
			backgroundColor: NEUTRAL[50],
			boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}`,
		},
	},
	"@media (prefers-reduced-motion: reduce)": {
		"& .MuiPickersOutlinedInput-root": { transition: "none" },
	},
});
